import { create } from 'zustand';

export interface CartItem {
  ticketTypeId: number;
  ticketTypeName: string;
  quantity: number;
  unitPrice: number;
  currency: string;
  maxPerOrder: number;
  minPerOrder: number;
  availableCount: number;
}

export interface TenantBranding {
  name: string;
  logo?: string;
  primaryColor: string;
  secondaryColor: string;
}

export interface BrandInfo {
  name: string;
  logo?: string;
  description?: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  socialLinks?: Record<string, string>;
  websiteUrl?: string;
  termsConditions?: string;
  privacyPolicy?: string;
  refundPolicy?: string;
}

interface PublicState {
  tenantBranding: TenantBranding | null;
  brandInfo: BrandInfo | null;
  isStorefrontLoaded: boolean;

  cartItems: CartItem[];
  cartEventId: number | null;
  cartEventSlug: string | null;
  couponCode: string | null;
  couponDiscount: { type: string; value: number; maxDiscount?: number } | null;

  buyerName: string;
  buyerEmail: string;
  buyerPhone: string;

  setStorefrontData: (tenant: TenantBranding, brand: BrandInfo | null) => void;
  addToCart: (eventId: number, eventSlug: string, item: CartItem) => void;
  updateQuantity: (ticketTypeId: number, quantity: number) => void;
  removeFromCart: (ticketTypeId: number) => void;
  clearCart: () => void;
  setCoupon: (code: string, discount: { type: string; value: number; maxDiscount?: number }) => void;
  clearCoupon: () => void;
  setBuyerInfo: (name: string, email: string, phone: string) => void;
  getCartTotal: () => number;
  getCartItemCount: () => number;
  getDiscountAmount: () => number;
}

export const usePublicStore = create<PublicState>((set, get) => ({
  tenantBranding: null,
  brandInfo: null,
  isStorefrontLoaded: false,

  cartItems: [],
  cartEventId: null,
  cartEventSlug: null,
  couponCode: null,
  couponDiscount: null,

  buyerName: '',
  buyerEmail: '',
  buyerPhone: '',

  setStorefrontData: (tenant, brand) =>
    set({ tenantBranding: tenant, brandInfo: brand, isStorefrontLoaded: true }),

  addToCart: (eventId, eventSlug, item) => {
    const state = get();
    // If different event, clear cart
    if (state.cartEventId && state.cartEventId !== eventId) {
      set({ cartItems: [item], cartEventId: eventId, cartEventSlug: eventSlug, couponCode: null, couponDiscount: null });
      return;
    }
    const existing = state.cartItems.find(i => i.ticketTypeId === item.ticketTypeId);
    if (existing) {
      set({
        cartItems: state.cartItems.map(i =>
          i.ticketTypeId === item.ticketTypeId ? { ...i, quantity: item.quantity } : i
        ),
        cartEventId: eventId,
        cartEventSlug: eventSlug,
      });
    } else {
      set({
        cartItems: [...state.cartItems, item],
        cartEventId: eventId,
        cartEventSlug: eventSlug,
      });
    }
  },

  updateQuantity: (ticketTypeId, quantity) => {
    if (quantity <= 0) {
      get().removeFromCart(ticketTypeId);
      return;
    }
    set({
      cartItems: get().cartItems.map(i =>
        i.ticketTypeId === ticketTypeId ? { ...i, quantity } : i
      ),
    });
  },

  removeFromCart: (ticketTypeId) =>
    set({ cartItems: get().cartItems.filter(i => i.ticketTypeId !== ticketTypeId) }),

  clearCart: () =>
    set({ cartItems: [], cartEventId: null, cartEventSlug: null, couponCode: null, couponDiscount: null, buyerName: '', buyerEmail: '', buyerPhone: '' }),

  setCoupon: (code, discount) =>
    set({ couponCode: code, couponDiscount: discount }),

  clearCoupon: () =>
    set({ couponCode: null, couponDiscount: null }),

  setBuyerInfo: (name, email, phone) =>
    set({ buyerName: name, buyerEmail: email, buyerPhone: phone }),

  getCartTotal: () => {
    const state = get();
    const subtotal = state.cartItems.reduce((sum, i) => sum + i.unitPrice * i.quantity, 0);
    const discount = state.getDiscountAmount();
    return Math.max(0, subtotal - discount);
  },

  getCartItemCount: () =>
    get().cartItems.reduce((sum, i) => sum + i.quantity, 0),

  getDiscountAmount: () => {
    const state = get();
    if (!state.couponDiscount) return 0;
    const subtotal = state.cartItems.reduce((sum, i) => sum + i.unitPrice * i.quantity, 0);
    if (state.couponDiscount.type === 'percentage') {
      let discount = (subtotal * state.couponDiscount.value) / 100;
      if (state.couponDiscount.maxDiscount) {
        discount = Math.min(discount, state.couponDiscount.maxDiscount);
      }
      return discount;
    }
    return Math.min(state.couponDiscount.value, subtotal);
  },
}));
